import { useEffect, useState } from "react";
import { Loader2, Search } from "lucide-react";
import VoiceCard from "@/components/jammable/VoiceCard";
import { searchVoices } from "@/lib/jammableApi";
import { safeApiError } from "@/lib/safeErrorText";

const field = "rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-[13px] text-white focus:border-fuchsia-300/50 focus:outline-none";

const cap = (c) => c.replace(/\b\w/g, (m) => m.toUpperCase());

/** Browse / search Jammable's public voices (name, kind, popular · trending · newest), 24 at a time. */
export default function VoiceSearch({ categories, selected, onPick, onSave, savedIds }) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [sort, setSort] = useState("popular");
  const [res, setRes] = useState({ voices: [], page: 1, total_pages: 1 });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const load = async (page, append) => {
    setBusy(true);
    setErr(null);
    try {
      const r = await searchVoices({ search: q.trim(), category: cat, sort, page });
      setRes((p) => ({ ...r, voices: append ? [...p.voices, ...r.voices] : r.voices }));
    } catch (e) { setErr(safeApiError(e, "Couldn't search voices")); }
    finally { setBusy(false); }
  };
  useEffect(() => {
    const t = setTimeout(() => load(1, false), 400);
    return () => clearTimeout(t);
  }, [q, cat, sort]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div data-testid="jam-voice-search">
      <div className="flex flex-wrap gap-2">
        <label className="relative min-w-[12rem] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
          <input data-testid="jam-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search voices by name" className={`${field} w-full pl-9`} />
        </label>
        <select data-testid="jam-search-category" value={cat} onChange={(e) => setCat(e.target.value)} className={field}>
          <option value="">All kinds</option>
          {categories.map((c) => <option key={c} value={c}>{cap(c)}</option>)}
        </select>
        <select data-testid="jam-search-sort" value={sort} onChange={(e) => setSort(e.target.value)} className={field}>
          <option value="popular">Most used</option>
          <option value="trending">Trending</option>
          <option value="newest">Newest</option>
        </select>
      </div>
      {res.notice ? <p data-testid="jam-search-notice" className="mt-3 rounded-xl border border-amber-300/25 bg-amber-500/[0.08] px-3 py-2 text-[12px] text-amber-50/85">{res.notice}</p> : null}
      {err ? <p data-testid="jam-search-error" className="mt-3 text-[12px] text-rose-300">{err}</p> : null}
      <div className="mt-3 grid max-h-72 gap-2 overflow-y-auto pr-1 sm:grid-cols-2">
        {res.voices.map((v) => <VoiceCard key={v.id} voice={v} selected={selected?.id === v.id} onPick={onPick} onSave={onSave} saved={savedIds.has(v.id)} />)}
        {!busy && !err && !res.voices.length ? <p data-testid="jam-search-empty" className="text-[12px] text-white/50">No voices match that search.</p> : null}
      </div>
      {busy ? <p className="mt-2 flex items-center gap-2 text-[12px] text-white/55"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…</p>
        : res.page < res.total_pages ? <button type="button" data-testid="jam-search-more" onClick={() => load(res.page + 1, true)} className="mt-2 font-mono text-[10px] uppercase tracking-wider text-fuchsia-200 hover:text-white">Show more voices</button> : null}
    </div>
  );
}
